/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { confirmActionWithSuspiciousItems } from 'Cloud/Application/Suspicious';
import { logger } from 'lib/logger';
import { clone as cloneRamda, path } from 'ramda';
import { downloadItem, downloadItemsZip } from 'reactApp/appHelpers/appDownload';
import { IS_BIZ_USER, IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { exclusivePublicSizeCalcEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicSizes';
import { sendAttachesGa } from 'reactApp/modules/attaches/attaches.analytics';
import { downloadAttachesByIds } from 'reactApp/modules/attaches/attaches.helpers';
import { type AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { downloadAttachItem } from 'reactApp/modules/attaches/attaches.utils';
import { getBizFilteredItems } from 'reactApp/modules/bizCategories/bizCategories.helpers';
import { getBizCurrentCategory } from 'reactApp/modules/bizCategories/bizCategories.selectors';
import { dispatchNewSearchRadar } from 'reactApp/modules/dwh/dwh.module';
import { getIsViewerFileVerifiedByKaspersky } from 'reactApp/modules/file/utils';
import { reDownloadController, ReDownloadEntry } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getStorage, isIntegrationStorage } from 'reactApp/modules/storage/storage.helpers';
import { getCurrentFolder as getCurrentFolderAction, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { store as reduxStore } from 'reactApp/store';
import { o2AuthedSourceLoader } from 'reactApp/ui/ReactViewer/O2AuthedSourceLoader';
import { ToolbarDownloadFrom } from 'reactApp/ui/Toolbar/Toolbar.types';
import { sendKasperskiSnackAnalitics } from 'reactApp/utils/kasperskiSnackGa';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { verifiedFileByAntivirusDesktopText } from './constants';
import { getSelectedItems } from './helpers/getSelectedItems';

export const download = ({
    useDownloadUrl = false,
    id,
    storage,
    from,
}: { useDownloadUrl?: boolean; id?: string; storage?: EStorageType; from?: ToolbarDownloadFrom } = {}): void => {
    const state = reduxStore.getState();
    const currentFolder = getCurrentFolderAction(state);
    let selected = getSelectedItems(state);
    const isTotalAllSelected = SelectionsSelectors.isTotalAllSelected(state) && exclusivePublicSizeCalcEnabled;

    /* tempexp_15344-start */
    if (from === ToolbarDownloadFrom.reDownload) {
        const reDownloadParams = reDownloadController.getParams(ReDownloadEntry.Toolbar);

        if (reDownloadParams) {
            selected = reDownloadParams.selected;
        }
    }
    /* tempexp_15344-end */

    const currentStorage = getCurrentStorage(state);
    const item = id && getStorageItemById(state, storage || currentStorage, id);

    if (item && !selected.length) {
        selected = [item];
    }

    if (isTotalAllSelected && currentFolder) {
        selected = [];
    }

    /* tempexp_15344-next-line */
    reDownloadController.setFutureParams(ReDownloadEntry.Toolbar, {
        useDownloadUrl,
        id,
        storage,
        selected,
    });

    const { isAttaches, isFavorites, isSearch, isHome, isPublic, isAlbums, isVigbo } = getStorage(currentStorage);

    // const isPaidUser = UserSelectors.isPaidUser(state);
    // const publicWeblink = getPublicRootWeblink(reduxStore.getState())?.weblink;
    // const downloadLimits = getPublicLimits(state, publicWeblink || '');
    // const notDownloaded = !IS_BIZ_USER && !IS_CORP_USER && !isPaidUser && downloadLimits?.left === 0;

    // const dataForPromo = {};

    // if (notDownloaded) {
    //     renderDownloadLimitsPromo(dataForPromo);
    //     return;
    // }

    if (!currentFolder && !selected?.length) {
        return;
    }

    sendPaymentGa({
        eventCategory: ECategoryGa.toolbar,
        action: 'download_content',
        source: currentStorage,
        count_files: selected.length || 1,
    });

    if (isSearch && from === ToolbarDownloadFrom.toolbar) {
        const dwhData = {
            eventCategory: ECategoryGa.toolbar_search,
            action: 'download-files',
            count_files: selected.length || 1,
        };
        const items = selected.map((item) => ({
            file_name: 'nameWithoutExt' in item && item.nameWithoutExt,
            file_id: item.id,
            pos: 'pos' in item && item.pos,
            type: item.kind,
        }));

        reduxStore.dispatch(dispatchNewSearchRadar({ dwhData, items }));
    }

    if (isAttaches && selected?.length) {
        sendAttachesGa('download-from-toolbar', selected.length.toString());

        selected.forEach((item) => sendAttachesGa('download', 'ext' in item ? item.ext : ''));

        if (selected.length === 1) {
            const attachType: string | undefined = path(['attachType'], selected[0]);
            const isCloudAttachType = attachType === EAttachTypes.cloud || attachType === EAttachTypes.cloudStock;

            if (isCloudAttachType) {
                downloadItem({ itemOrId: selected[0], useDownloadUrl, storage: storage || currentStorage });
            } else {
                downloadAttachItem(selected[0] as AttachesItem).then(() => {
                    if (getIsViewerFileVerifiedByKaspersky(state)) {
                        /**
                         * Показываем снэк только для пабликов и для аттачей(CLOUDWEB-14706)
                         * и самым последним этапом скачивания, что бы не мешался другим модалкам на тача
                         * (Пришлось его продублировать тут, т.к. аттачи на таче скачиваются иначе, чем всё остальное)
                         */
                        snackbarController.showSnackbar({
                            id: 'protected-download',
                            closable: true,
                            title: 'Скачивание началось',
                            text: IS_MOBILE_BROWSER ? 'Файл проверен и безопасен для вашего смартфона' : verifiedFileByAntivirusDesktopText,
                            type: SnackbarTypes.protect,
                            onShow: () => {
                                sendKasperskiSnackAnalitics({ action: 'view' });
                            },
                            onClose: () => {
                                sendKasperskiSnackAnalitics({ action: 'close' });
                            },
                        });
                    }
                });
            }
        } else {
            sendAttachesGa('download-multiple');
            confirmActionWithSuspiciousItems(cloneRamda(selected), 'download').then(function () {
                downloadAttachesByIds(selected.map((item) => item.id));
            });
        }

        return;
    }

    if ((isFavorites || isSearch) && !selected.length && currentFolder && 'list' in currentFolder) {
        const items = Object.values(currentFolder.list);
        const bizCurrentCategory = getBizCurrentCategory(state);
        const bizFilteredItems = getBizFilteredItems(items, bizCurrentCategory);
        downloadItemsZip({
            items: IS_BIZ_USER ? bizFilteredItems : items,
            name: isFavorites ? 'favorites' : 'archive',
        });
        return;
    }

    if (isIntegrationStorage(currentStorage) && selected.length === 1) {
        const [cloudItem] = selected;

        if (cloudItem.isFolder) {
            downloadItemsZip({ items: selected, name: cloudItem.name });
            return;
        }

        if (!cloudItem.url?.get) {
            logger.error('[Integration::download] No download url');
            return;
        }

        o2AuthedSourceLoader
            .saveFile({
                ...cloudItem,
                url: {
                    get: cloudItem.url.get,
                },
            })
            .catch((error) => {
                logger.error('[Integration::download]', error);
            });

        return;
    }

    if (selected.length === 1) {
        if (selected[0].isFolder) {
            downloadItemsZip({ items: selected, name: selected[0].name });
        } else {
            downloadItem({ itemOrId: selected[0], useDownloadUrl, storage: currentStorage });
        }

        return;
    }

    const name = (isHome || isPublic || isAlbums || isVigbo) && currentFolder && 'name' in currentFolder ? currentFolder?.name : 'archive';
    downloadItemsZip({ items: selected.length ? selected : [currentFolder], name });
};
